import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import BSPagination from "../../Components/pagination";
import LoadingSpinner from "../../Components/spinner";
import {
  changeBugStatus,
  getPaginatedGameBugs,
  searchGameBugs,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import { useNavigate } from "react-router-dom";
import BugDetailsModal from "./bugsDetailsModal";
import { toast } from "sonner";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const GameBugsList = ({
  gameBugs,
  setGameBugs,
  pagination,
  setPagination,
  granted,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filtering, setFiltering] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [modalOptions, setModalOptions] = useState({});
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const tableRef = useRef(null);

  // Fetch Game Bugs
  const fetchGameBugs = async (currentPage, pageSize) => {
    setFiltering(true);
    const res = await getPaginatedGameBugs(currentPage, pageSize, query);
    if (res?.data?.success && res?.data?.data) {
      setGameBugs(res.data.data);
      res.data.pagination && setPagination(res.data.pagination);
    } else {
      handleErrors(res, navigate);
    }
    setFiltering(false);
  };

  // Pagination handler
  const handlePageChange = (page) => {
    const temp = { ...pagination };
    setPagination((prev) => ({ ...prev, currentPage: page }));
    temp.currentPage = page;
    fetchGameBugs(page, temp.pageSize);
  };

  // Search handler
  const handleSearch = async (e) => {
    const v = e.target.value;
    setQuery(v);
    if (v) {
      setFiltering(true);
      const res = await searchGameBugs(v, 1, pagination.pageSize);
      if (res?.data?.data) {
        setGameBugs(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else {
        handleErrors(res, navigate, "Search Bugs:");
      }
      setFiltering(false);
    } else {
      fetchGameBugs(pagination);
    }
  };

  // Detail modal
  const handleOpenDetailsModal = (bug) => {
    setSelectedData(bug);
    setShowDetailsModal(true);
  };

  // Change status modal
  const handleStatusChange = (bug) => {
    if (!granted?.some((p) => p.type === "update")) {
      toast.error(
        "You don't have neccessary permissions to do this operation..",
        { duration: 3000 }
      );
      return;
    }
    setModalOptions({
      header: "Change Status",
      body: `Mark this bug as ${bug.resolved ? "un-resolved" : "resolved"}?`,
      okay: "Change",
      method: changeStatus,
      data: { id: bug.id, resolved: bug.resolved ? 0 : 1 },
    });
    setShowOptionsModal(true);
  };
  const changeStatus = async (data) => {
    const res = await changeBugStatus(data);
    if (res?.data?.success) {
      fetchGameBugs(pagination?.currentPage, pagination?.pageSize);
    }
    setShowOptionsModal(false);
  };

  // DataTable setup / redraw
  useEffect(() => {
    const rows = gameBugs.map((bug) => {
      const message = `<div class="tb-preview">${bug.message}</div>`;
      const game = bug.related_game?.title || "";
      const reporter = bug.reported_by?.email || "";
      const resolved = `
        <span class="${bug.resolved ? "text-success" : "text-danger"}" data-id="${bug.id}">
          ${bug.resolved ? "Yes" : "No"}
        </span>`;
      const actions =
        `<button class="btn btn-sm btn-outline-success me-2 view-btn" data-id="${bug.id}" title="view details">
           <i class="fas fa-eye"></i>
         </button>` +
        (bug.resolved
          ? `<button class="btn btn-sm btn-outline-info mark-btn" data-id="${bug.id}" title="mark as un-resolved">
               <i class="fas fa-check"></i>
             </button>`
          : `<button class="btn btn-sm btn-outline-danger mark-btn" data-id="${bug.id}" title="mark as resolved">
               <i class="fas fa-times"></i>
             </button>`);

      return [message, game, reporter, resolved, actions];
    });

    const cfg = {
      data: rows,
      columns: [
        { title: "Bug" },
        { title: "Game" },
        { title: "Reported By" },
        { title: "Resolved", orderable: false },
        { title: "Action", orderable: false },
      ],
      destroy: true,
      responsive: true,
      paging: false,
      searching: false,
      pageLength: pagination.pageSize || 10,
    };

    if ($.fn.dataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().clear().rows.add(rows).draw();
    } else {
      $(tableRef.current).DataTable(cfg);
    }

    const $tbl = $(tableRef.current);
    $tbl
      .off("click", ".view-btn")
      .on("click", ".view-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleOpenDetailsModal(gameBugs.find((b) => b.id === id));
      })
      .off("click", ".mark-btn")
      .on("click", ".mark-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleStatusChange(gameBugs.find((b) => b.id === id));
      });

    return () => {
      if ($.fn.dataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
    };
  }, [gameBugs, granted]);

  return (
    <div className="container py-4 text-light bg-dark rounded mb-4">
      <div className="row mb-3 align-items-center">
        <div className="col-md-4">
          <Form.Control
            type="text"
            placeholder="Search Game Bugs..."
            value={query}
            onChange={handleSearch}
            className="bg-dark text-light border-light"
          />
        </div>
        <div className="col-md-2">
          {filtering && <LoadingSpinner size="md" />}
        </div>
      </div>

      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-dark w-100"
        />
      </div>

      <div className="d-flex justify-content-end mt-2">
        <div className="mx-4 py-2">
          <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
        </div>
        <BSPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast
        />
      </div>

      <BugDetailsModal
        showDetailsModal={showDetailsModal}
        setShowDetailsModal={setShowDetailsModal}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
      />

      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
        className="text-light"
      >
        <Modal.Header closeButton className="bg-dark border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">{modalOptions.body}</Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button
            variant="secondary"
            onClick={() => setShowOptionsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .tb-preview {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          max-height: 4.5em;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default GameBugsList;
